import { useNavigate } from "react-router";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { AutocompleteInput } from "src/components/AutocompleteInput/AutocompleteInput";
import { AutocompleteList } from "src/components/AutocompleteList/AutocompleteList";
import { ButtonBase } from "src/components/Button/ButtonBase";
import { Portal } from "src/components/Portal/Portal";
import color from "src/design-system/tokens/color";
import { spacing } from "src/design-system/tokens/spacing";
import X from "src/design-system/icons/X.svg?react";
import useAutocomplete from "src/utils/hooks/useAutocomplete";
import { useTypedLocation } from "src/utils/hooks/useTypedLocation";
import { navigateToNewState } from "src/utils/location/navigateToNewState";
import styled from "styled-components";
import { iconSize } from "src/theme";

type Props = {
  onSelectOption: (place: AutocompletePlace) => void;
  initialQuery?: string;
  handleClose?: () => void;
  places?: string[];
};

export function TripPlannerAutocomplete(props: Props) {
  const navigate = useNavigate();
  const location = useTypedLocation();
  const autocompleteState = location.state?.autocomplete!;
  const { results, query, changeQuery } = useAutocomplete(
    props.initialQuery,
    Boolean(props.initialQuery),
    true,
    props.places,
  );

  const handleAutocompleteSelect = (place: AutocompletePlace) => {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:AutocompleteOption",
    });
    props.onSelectOption(place);
  };

  const handleCloseClick = () => {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:Close",
    });
    navigateToNewState(navigate, { editTripPlan: false }, location);
    props.handleClose?.();
  };

  return (
    <Portal>
      <Screen>
        <Header>
          <StyledAutocompleteInput
            type={autocompleteState}
            value={query}
            onChange={changeQuery}
            // We can disable this autoFocus rule because we only ever have one instance
            // in the DOM at any one time.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <CloseWrapper onClick={handleCloseClick}>
            <span className="sr-only">close</span>
            <X
              width={iconSize.sm}
              height={iconSize.sm}
              color={color.icon.primary}
            />
          </CloseWrapper>
        </Header>
        <AutocompleteList
          isInputFocused={true}
          results={results}
          onSelectPlace={handleAutocompleteSelect}
        />
      </Screen>
    </Portal>
  );
}

const Screen = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  background-color: ${color.bg.surface.secondary};
  padding: ${spacing.xl};
  /* z-index should be higher than the SearchEdit modal. */
  z-index: 10001;
  overflow-y: auto;
`;

const Header = styled.div`
  margin-bottom: ${spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledAutocompleteInput = styled(AutocompleteInput)`
  flex: 1 0 auto;
`;

const CloseWrapper = styled(ButtonBase)`
  vertical-align: middle;
  padding-left: ${spacing.xl};
  margin-right: ${spacing.md};
`;
